"use client";

import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { useState } from "react";
import BlurFadeText from "@/components/common/blurFadeText";
import SectionEyebrow from "@/components/common/sectionEyebrow";
import SectionTitle from "@/components/common/sectionTitle";
import FAIcon from "@/components/icons/faIcon";

const EASE_OUT = [0.22, 1, 0.36, 1];

function revealProps(shouldReduceMotion, delay = 0, axis = "y", distance = 24) {
  if (shouldReduceMotion) {
    return {};
  }

  const hidden = { opacity: 0, filter: "blur(10px)" };

  if (axis === "x") {
    hidden.x = distance;
  } else if (axis === "-x") {
    hidden.x = -distance;
  } else {
    hidden.y = distance;
  }

  return {
    initial: hidden,
    whileInView: { opacity: 1, x: 0, y: 0, filter: "blur(0px)" },
    viewport: { once: true, amount: 0.22 },
    transition: { duration: 0.78, delay, ease: EASE_OUT },
  };
}

const FINISHED_ROWS = [
  {
    title: "Connected compliance workflows",
    description:
      "Move from audits and assessments to actions, reviews and closure through a structured workflow designed to keep work progressing. Every activity is linked, creating a clear trail from issue identification through to resolution.",
    features: ["Audit management", "Action tracking", "Structured workflows"],
    panel: "workflow",
    image: "/images/platform/incidents-overview.webp",
  },
  {
    title: "Operational dashboards and visibility",
    description:
      "Provide teams with instant access to outstanding actions, upcoming reviews, incidents and compliance activity. Centralised dashboards help users understand what needs attention without searching across multiple systems.",
    features: [
      "Live dashboards",
      "Outstanding actions",
      "Compliance visibility",
    ],
    panel: "dashboard",
    image: "/images/platform/sml-charting.webp",
  },
  {
    title: "Controlled access and accountability",
    description:
      "Manage users, permissions and responsibilities across multiple sites and teams. Role-based access helps ensure the right people have access to the right information while maintaining accountability across the organisation.",
    features: ["Role-based permissions", "User management", "Audit trails"],
    panel: "account",
    image: "/images/platform/sml-access.webp",
  },
];

export function VariantB_FinishedProduct() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="mx-auto w-full max-w-328 px-4 sm:px-6 lg:px-0 ">
      <motion.div
        {...revealProps(shouldReduceMotion, 0.04, "y", 18)}
        className="max-w-4xl"
      >
        <SectionTitle
          className="text-[2.6rem] sm:text-[3.2rem]"
          text="Start with a system your team can actually use."
        />
        <p className="mt-5 max-w-3xl text-base leading-8 text-[#06040c]/62 sm:text-[1.05rem]">
          SML Connect brings audits, risk assessments, incidents, actions and
          compliance activity together in one connected platform. Designed for
          real operational environments, it gives managers, consultants and site
          teams the visibility and structure needed to stay on top of health and
          safety responsibilities every day.
        </p>
      </motion.div>

      <div className="mt-12 space-y-6">
        {FINISHED_ROWS.map((row, index) => (
          <motion.div
            key={row.title}
            {...revealProps(shouldReduceMotion, 0.1 + index * 0.08, "y", 20)}
            className={`flex flex-col gap-6 rounded-lg bg-white p-6  sm:p-8 lg:flex-row lg:items-center lg:gap-20 min-h-[450px] ${
              index % 2 === 1 ? "lg:flex-row-reverse" : ""
            }`}
          >
            <div className="flex-1">
              <h3 className="mt-3 font-raleway text-[1.8rem] font-semibold leading-[0.96] tracking-tighter text-[#06040c] sm:text-[2.2rem]">
                {row.title}
              </h3>
              <p className="mt-4 max-w-lg text-base leading-7 text-[#06040c]/62">
                {row.description}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {row.features.map((f) => (
                  <span
                    key={f}
                    className="rounded-full bg-neutral-100 px-4 py-2 text-sm font-medium text-[#06040c]/70"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="relative aspect-3/2 w-full overflow-hidden rounded-lg">
                <Image
                  src={row.image}
                  alt={row.title}
                  fill
                  className="object-contain scale-101"
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const FIELD_FEATURES_A = [
  {
    icon: "file-contract",
    title: "Core safety processes",
    description:
      "Create, schedule and manage audits, risk assessments and incidents through configurable workflows that keep records, actions and evidence connected.",
    items: [
      "Audit scheduling with configurable templates",
      "Risk assessment creation through to review",
      "Incident reporting and investigation tracking",
      "Hazard identification and risk rating",
    ],
  },
  {
    icon: "calendar-check",
    title: "Operational management",
    description:
      "Track corrective actions and recurring checks across audits, incidents and operational activity from a central dashboard.",
    items: [
      "Central action management with ownership",
      "Checklist scheduling and completion monitoring",
      "Deadline tracking and overdue alerts",
      "Performance trends and completion rates",
    ],
  },
  {
    icon: "chart-line",
    title: "Governance and oversight",
    description:
      "Manage customers, sites and users through a structured environment with dashboards and reporting that provide visibility across the organisation.",
    items: [
      "Multi-site customer and account management",
      "KPI and compliance dashboards",
      "Role-based permissions and access control",
      "Structured user management and audit trails",
    ],
  },
];

export function VariantA_FieldWorkflows() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="mx-auto w-full max-w-328 space-y-16">
      <motion.div {...revealProps(shouldReduceMotion)} className="max-w-4xl">
        <SectionTitle
          className="text-[2.6rem] sm:text-[3.2rem]"
          text="One platform. Every critical safety process."
        />
        <p className="mt-5 max-w-3xl text-base leading-8 text-[#06040c]/62 sm:text-[1.05rem]">
          SML Connect brings together the core activities required to manage
          health and safety across sites, teams and organisations. Rather than
          relying on disconnected systems, information is managed through a
          single platform that keeps records, actions and reporting connected.
        </p>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-3">
        {FIELD_FEATURES_A.map((group, index) => (
          <motion.article
            key={group.title}
            {...revealProps(
              shouldReduceMotion,
              0.08 + index * 0.05,
              index % 2 === 0 ? "-x" : "x",
              18,
            )}
            className="group relative overflow-hidden rounded-lg  bg-white p-7 transition-all duration-300 sm:p-8"
          >
            <div className="absolute -right-6 -top-6 text-[6rem] text-[#06040c]/4">
              <FAIcon icon={group.icon} noMargin />
            </div>
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <FAIcon icon={group.icon} noMargin size={22} />
              </div>
              <h3 className="mt-5 font-raleway text-[1.7rem] font-semibold leading-[0.96] tracking-tighter text-[#06040c] sm:text-[2rem]">
                {group.title}
              </h3>
              <p className="mt-4 text-base leading-7 text-[#06040c]/62">
                {group.description}
              </p>
              <div className="mt-6 space-y-2.5">
                {group.items.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 rounded-lg bg-neutral-50 px-4 py-3 text-sm leading-6 text-[#06040c]/74 sm:text-base"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/12 text-[0.6rem] text-primary">
                      <FAIcon icon="check" noMargin />
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

const FIELD_TABS = [
  {
    id: "control",
    label: "Operational control",
    icon: "wrench",
    content: {
      description:
        "Keep daily safety activity organised and visible through a centralised platform designed around action management, accountability and progress tracking.",
      items: [
        "Central action management",
        "Outstanding and overdue work monitoring",
        "Ownership and responsibility tracking",
        "Activity history across records",
      ],
    },
  },
  {
    id: "audits",
    label: "Audits and assessments",
    icon: "file-contract",
    content: {
      description:
        "Plan, deliver and review audits and risk assessments through structured workflows designed to improve consistency and visibility across every site.",
      items: [
        "Configurable audit templates",
        "Risk assessment management",
        "Scoring and compliance tracking",
        "Review and follow-up workflows",
      ],
    },
  },
  {
    id: "incidents",
    label: "Incident management",
    icon: "bolt",
    content: {
      description:
        "Capture incidents, manage investigations and track corrective actions through to closure, ensuring nothing is lost in email chains or spreadsheets.",
      items: [
        "Incident reporting workflows",
        "Investigation management",
        "Corrective action tracking",
        "Closure and review processes",
      ],
    },
  },
  {
    id: "compliance",
    label: "Scheduled compliance",
    icon: "calendar-check",
    content: {
      description:
        "Manage recurring inspections, checks and operational tasks through planned schedules and completion monitoring.",
      items: [
        "Checklist scheduling",
        "Recurring compliance activities",
        "Completion monitoring",
        "Missed and overdue tracking",
      ],
    },
  },
  {
    id: "reporting",
    label: "Reporting and performance",
    icon: "chart-line",
    content: {
      description:
        "Turn operational activity into meaningful insights through dashboards, KPI reporting and organisation-wide visibility.",
      items: [
        "KPI dashboards",
        "Performance monitoring",
        "Compliance trend reporting",
        "Site and customer comparisons",
      ],
    },
  },
];

export function VariantB_FieldWorkflows() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("control");

  const currentTab = FIELD_TABS.find((t) => t.id === activeTab);

  return (
    <section className="mx-auto w-full max-w-328 space-y-10">
      <motion.div {...revealProps(shouldReduceMotion)} className="max-w-3xl">
        <SectionEyebrow
          text="Built for real use"
          className="pt-0"
          textClassName="text-black/52"
          amount={0.35}
        />
        <SectionTitle
          className="mt-3 text-[2.6rem] sm:text-[3.2rem]"
          text="Useful in the field, structured enough for management and governance."
        />
        <p className="mt-4 max-w-3xl text-base leading-7 text-[#06040c]/62 sm:text-[1.02rem] sm:leading-8">
          <BlurFadeText
            text="SML Connect supports day-to-day safety operations while providing the visibility needed to monitor performance, compliance and risk across the organisation."
            delay={0.16}
            blur={8}
            y={16}
          />
        </p>
      </motion.div>

      {}
      <div className="grid grid-cols-2 gap-2 rounded-full bg-white p-1.5 sm:flex">
        {FIELD_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-1 items-center justify-center gap-2.5 whitespace-nowrap rounded-full px-5 py-3 text-sm font-semibold transition-all duration-300 ${
              activeTab === tab.id
                ? "bg-primary text-black shadow-sm"
                : "text-[#06040c]/60 hover:text-[#06040c] hover:bg-neutral-100"
            }`}
          >
            <FAIcon icon={tab.icon} noMargin size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {}
      {currentTab && (
        <motion.div
          key={currentTab.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE_OUT }}
          className="overflow-hidden rounded-lg bg-white"
        >
          <div className="p-7 sm:p-8 lg:p-12">
            <div className="flex items-center gap-4">
              <h3 className="font-raleway text-[1.7rem] font-semibold leading-[0.96] tracking-tighter text-[#06040c] sm:text-[2rem]">
                {currentTab.label}
              </h3>
            </div>
            <p className="mt-5 max-w-3xl text-base leading-7 text-[#06040c]/62 sm:text-[1.02rem] sm:leading-8">
              {currentTab.content.description}
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {currentTab.content.items.map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-md border border-zinc-200 px-4 py-3.5 text-sm leading-6 text-[#06040c]/74 sm:text-base"
                >
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[0.6rem] text-primary">
                    <FAIcon icon="check" noMargin />
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </section>
  );
}
